/**
 * Tìm theo từ khoá trong bản xem trước — APM-PVW-d. Hàm thuần + dựng `Range`.
 *
 * So khớp **không phân biệt hoa thường và không phân biệt dấu**: gõ "thiet ke"
 * thấy "Thiết kế", gõ "d" thấy "đ" — cùng luật tìm kiếm tiếng Việt của repo
 * (cạm bẫy "Search tiếng Việt bằng LIKE" trong CLAUDE.md).
 *
 * Gập **từng ký tự** kèm bảng ánh xạ về chỉ số gốc: bỏ dấu đổi độ dài chuỗi
 * (`ế` dựng sẵn là 1 ký tự, NFD là 3), nên gập cả chuỗi rồi lấy vị trí là tô
 * lệch chữ. Cùng cách với `chilldoc/find/find-replace.ts` (trình soạn) — hai
 * bên nên gộp một khi bản ấy vào repo.
 */

const COMBINING = /\p{M}/gu;

/** Số kết quả tối đa — tài liệu dài với một chữ cái phổ biến không được treo luồng chính. */
export const MAX_SEARCH_RESULTS = 2000;

function foldChar(char: string): string {
  return char
    .normalize('NFD')
    .replace(COMBINING, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/** Chuỗi đã gập + `map[i]` = chỉ số trong chuỗi gốc của ký tự gập thứ `i`. */
export function foldText(text: string): {
  readonly folded: string;
  readonly map: readonly number[];
} {
  let folded = '';
  const map: number[] = [];
  for (let index = 0; index < text.length; index++) {
    const piece = foldChar(text[index]!);
    folded += piece;
    for (let k = 0; k < piece.length; k++) map.push(index);
  }
  return { folded, map };
}

export interface TextMatch {
  /** `[start, end)` theo chỉ số của chuỗi **gốc**. */
  readonly start: number;
  readonly end: number;
}

/** Mọi chỗ khớp, không chồng nhau, theo thứ tự; tối đa `limit`. Từ khoá rỗng ⇒ không có gì. */
export function findAll(text: string, query: string, limit = MAX_SEARCH_RESULTS): TextMatch[] {
  const needle = foldText(query.trim()).folded;
  if (!needle) return [];
  const { folded, map } = foldText(text);
  const found: TextMatch[] = [];
  let at = folded.indexOf(needle);
  while (at !== -1 && found.length < limit) {
    const tail = at + needle.length;
    const start = map[at]!;
    const end = tail < folded.length ? map[tail]! : text.length;
    if (end > start) found.push({ start, end });
    at = folded.indexOf(needle, Math.max(tail, at + 1));
  }
  return found;
}

/** Thẻ không mang chữ người đọc thấy — `<style>` của docx-preview nằm ngay trong khung. */
const SKIP = new Set(['STYLE', 'SCRIPT', 'NOSCRIPT', 'TEMPLATE', 'SVG']);
const BLOCK = new Set([
  'P',
  'DIV',
  'LI',
  'UL',
  'OL',
  'TD',
  'TH',
  'TR',
  'TABLE',
  'PRE',
  'BLOCKQUOTE',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'FIGURE',
  'FIGCAPTION',
  'DT',
  'DD',
  'BR',
  'HR',
]);

/** Chữ của cả một cây DOM nối lại, kèm chỗ bắt đầu của từng text node trong chuỗi ấy. */
export interface TextIndex {
  readonly text: string;
  readonly nodes: readonly Text[];
  readonly starts: readonly number[];
}

/**
 * Nối chữ của mọi text node nhìn thấy dưới `root` theo thứ tự tài liệu. Giữa hai
 * **khối** khác nhau chèn `\n` (không ứng với node nào): không có nó thì cuối một
 * đoạn dính vào đầu đoạn sau và "kết" + "luận" ở hai đoạn thành một kết quả ma.
 * Trong cùng một khối thì nối liền — chữ đậm giữa từ ("thi**ết** kế") vẫn khớp.
 */
export function indexText(root: Node): TextIndex {
  const nodes: Text[] = [];
  const starts: number[] = [];
  let text = '';
  let lastBlock: Element | null = null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      for (let el = node.parentElement; el && el !== root.parentElement; el = el.parentElement) {
        if (SKIP.has(el.tagName.toUpperCase()) || el.hidden) return NodeFilter.FILTER_REJECT;
      }
      return node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const block = blockOf(node, root);
    if (nodes.length > 0 && block !== lastBlock) text += '\n';
    lastBlock = block;
    nodes.push(node);
    starts.push(text.length);
    text += node.nodeValue ?? '';
  }
  return { text, nodes, starts };
}

function blockOf(node: Node, root: Node): Element | null {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (BLOCK.has(el.tagName.toUpperCase())) return el;
    if (el === root) return el;
  }
  return null;
}

/** Vị trí trong chuỗi nối ⇒ (text node, offset). Tìm nhị phân trên `starts`. */
function pointAt(index: TextIndex, offset: number, isEnd: boolean): [Text, number] {
  let low = 0;
  let high = index.starts.length - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    // Điểm cuối đúng bằng đầu một node thì thuộc về node trước (kết thúc của nó).
    if (index.starts[mid]! < offset || (!isEnd && index.starts[mid] === offset)) low = mid;
    else high = mid - 1;
  }
  return [index.nodes[low]!, offset - index.starts[low]!];
}

/** Chỗ khớp ⇒ `Range` (vắt qua nhiều text node được — `<b>` giữa từ). */
export function rangesOf(index: TextIndex, matches: readonly TextMatch[]): Range[] {
  if (index.nodes.length === 0) return [];
  return matches.map((match) => {
    const range = document.createRange();
    const [startNode, startOffset] = pointAt(index, match.start, false);
    const [endNode, endOffset] = pointAt(index, match.end, true);
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  });
}
